// UserProfilePage.tsx

import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { Dropdown } from "primereact/dropdown";
import { useParams } from "react-router-dom";
import Header from "./Includes/Header";
import Footer from "./Includes/Footer";
import HelpBtn from "./Includes/HelpBtn";
import Spacer from "MyComponents/UI/Spacer";
import address_icon from "../images/address_icon.png";
import user_icon from "../images/reg_user.png";
import countriesData from "../data/countries.json";
import countriesStateData from "../data/states.json";

import { getUserData } from "Utils/user";
import { notifyError, notifySuccess, ToastBox } from "MyComponents/Toaster";
import { SpinnerBox } from "./Spinner";
import config from "../config";

import axios from "axios";

interface FormData {
  firstName: string;
  lastName: string;
  phoneno: string;
  email: string;
  city: string;
  postalCode: string;
  streetAddress: string;
  country: string;
  state: string;
}

const UserProfilePage: React.FC = () => {
  const { userid } = useParams();
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    phoneno: "",
    email: "",
    city: "",
    postalCode: "",
    streetAddress: "",
    country: "",
    state: "",
  });

  const [stateValidation, setStateValidation] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: any }>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const saveAddressDetails_baseURL =
    config.NODE_API_BASE_URL + "/wso2/saveAddressDetails";

  const startLoading = () => setLoading(true);
  const stopLoading = () => setLoading(false);

  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "visible";
  }, [loading]);

  function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  useEffect(() => {
    const fetchUser = async () => {
      try {
        startLoading();
        const data = await getUserData(userid);
        const parsedUser = {
          firstName: data?.name?.givenName || "",
          lastName: data?.name?.familyName || "",
          email: data?.emails?.[0] || "",
          phoneno: data?.phoneNumbers?.[0]?.value || "",
          city: data?.["urn:scim:wso2:schema"]?.city || "",
          postalCode: data?.["urn:scim:wso2:schema"]?.personPostalCode || "",
          streetAddress:
            Array.isArray(data?.addresses) && data.addresses.length > 0
              ? data.addresses[0].formatted
              : undefined,
          country: capitalizeFirstLetter(
            data?.["urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"]
              ?.country || ""
          ),
          state: data?.["urn:scim:wso2:schema"]?.personStateOrProvince || "",
        };

        setFormData(parsedUser);
        stopLoading();
      } catch (err) {
        console.log("error", err);
        notifyError("Failed to load user data");
      }
    };

    fetchUser();
  }, [userid]);

  useEffect(() => {
    const errors: { [key: string]: string } = {
      firstName: validateFirstName(formData.firstName) || "",
      lastName: validateLastName(formData.lastName) || "",
      phoneno: validatePhone(formData.phoneno) || "",
      streetAddress: validateStreetAddress(formData.streetAddress) || "",
      postalCode: validatePostalCode(formData.postalCode) || "",
      city: validateCity(formData.city) || "",
      country: validateCountry(formData.country) || "",
      state: validateState(formData.state) || "",
    };    

    setFormErrors(errors);
    const isValid = Object.values(errors).every((err) => !err);
    setIsFormValid(isValid);
  }, [formData]);

  // const handleChange = (
  //   e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  // ) => {
  //   setFormData({ ...formData, [e.target.name]: e.target.value });
  // };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    let error:any;
    switch (name) {
      case "firstName":
        error = validateFirstName(value);
        break;
      case "lastName":
        error = validateLastName(value);
        break;
      case "streetAddress":
        error = validateStreetAddress(value);
        break;
      case "city":
        error = validateCity(value);
        break;
      case "postalCode":
        error = validatePostalCode(value);
        break;
      case "phoneno":
        error = validatePhone(value);
        break;
      default:
        error = undefined;
    }

    setFormErrors((prev:any) => ({ ...prev, [name]: error }));
  };

  // const handleStateSelect = (stateName: string) => {
  //   setFormData({ ...formData, state: stateName });
  //   setStateValidation(false);
  // };


  const handleCountrySelect = (countryValue: any) => {
    const name = countryValue?.name;
    setFormData({ ...formData, country: name });
    const error = validateCountry(name);
    setFormErrors((prev) => ({ ...prev, country: error }));
  };

  const handleStateSelect = (stateName: string) => {
    setFormData({ ...formData, state: stateName });
    const error = validateState(stateName);
    setFormErrors((prev) => ({ ...prev, state: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    startLoading();
    e.preventDefault();
    // if (!formData.state) {
    //   setStateValidation(true);
    //   return;
    // }
    const saveAddressResponse = await saveAddressDetails();
    if (saveAddressResponse?.status) notifySuccess("profile updated.");
    stopLoading();
  };

  const countryOptionTemplate = (option: { name: string; image: string }) => (
    <div className="flex align-items-center">
      {option.image && (
        <img
          src={option.image}
          alt={option.name}
          style={{ width: "20px", marginRight: "5px" }}
        />
      )}
      {option.name}
    </div>
  );

  const options = countriesData.map((country) => ({
    name: country.name,
    image: country.image,
  }));

  const countryValueTemplate = (option: any) => {
    if (option) {
      // setCountryValidation(false);
      // dispatch(setSelectedCountry(option.name));
      // setSelectedCountryState(option.name);
      return (
        <div className="flex align-items-center">
          {option.image && (
            <img
              src={option.image}
              alt={option.name}
              style={{ width: "20px", marginRight: "5px" }}
            />
          )}
          {option.name}
        </div>
      );
    } else {
      let imageSrc = countriesData.find(
        (country) => country.name === formData.country
      )?.image;
      return formData.country ? (
        <div className="flex align-items-center">
          <img
            src={imageSrc}
            alt={formData.country}
            style={{ width: "20px", marginRight: "5px" }}
          />
          {formData.country}
        </div>
      ) : (
        "Select a country"
      );
    }
  };

  const convertToCamelCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const saveAddressDetails = async () => {
    try {
      const response = await axios.post(saveAddressDetails_baseURL, {
        email: formData.email,
        country: formData.country,
        streetAddress: formData.streetAddress,
        postalCode: formData.postalCode,
        city: formData.city,
        firstName: convertToCamelCase(formData.firstName),
        lastName: convertToCamelCase(formData.lastName),
        state: formData.state || "",
        phoneNo:formData.phoneno
      });

      return response?.data;
    } catch (error: any) {
      notifyError("failed to update");
      stopLoading();
      console.error(error);
    }
  };

  const validateFirstName = (value: string) =>
    !value ? "First Name is required" : undefined;
  const validateLastName = (value: string) =>
    !value ? "Last Name is required" : undefined;
  const validateStreetAddress = (value: string) =>
    !value ? "Street address is required" : undefined;
  const validatePostalCode = (value: string) =>
    !value ? "Postal code is required" : undefined;
  const validateCity = (value: string) =>
    !value ? "City is required" : undefined;
  const validatePhone = (value: string) =>
    !value ? "Phone number is required" : undefined;
  const validateCountry = (value: string) =>
    !value ? "Country is required" : undefined;
  const validateState = (value: string) =>
    !value ? "State is required" : undefined;

  return (
    <>
      <ToastBox />
      <Header />
      {loading && <SpinnerBox />}
      <Container fluid>
        <Row className="px-5 mt-4">
          <Spacer height="30px" />
        </Row>
        <Row className="px-5">
          <Col md={12} className="text-center">
            <img src={user_icon} width={50} />
            <h3 className="text-center mt-3">User Profile</h3>
            <span className="text-secondary">
              View or update your profile details
            </span>
          </Col>
        </Row>

        <Row>
          <Col md={3}></Col>
          <Col md={6} className="mb-5 mt-4 rounded-3">
            <Form className="p-3" noValidate onSubmit={handleSubmit}>
              <Row className="mt-1">
                <Col md={6}>
                  <label>First Name</label>
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-1"
                    required
                  />
                  {formErrors?.firstName && (
                    <small className="text-danger">
                      {formErrors.firstName}
                    </small>
                  )}
                </Col>
                <Col md={6}>
                  <label>Last Name</label>
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-1"
                    required
                  />
                  {formErrors.lastName && (
                    <small className="text-danger">{formErrors.lastName}</small>
                  )}
                </Col>
              </Row>

              <Row className="mt-1">
                <Col md={6}>
                  <label>Phone Number</label>
                  <input
                    name="phoneno"
                    value={formData.phoneno}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-1"
                    required
                  />
                  {formErrors.phoneno && (
                    <small className="text-danger">{formErrors.phoneno}</small>
                  )}
                </Col>
                <Col md={6}>
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control bg-light rounded-5 mt-1"
                    value={formData.email}
                    disabled
                  />
                </Col>
              </Row>
              <div className="mb-1 mt-4">
                <img src={address_icon} width={22} />
                <label className="px-2 fw-bold">Address Information</label>
              </div>

              <Row className="mt-1">
                <Col md={6}>
                  <label>Street Address</label>
                  <input
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-2"
                  />
                  {formErrors.streetAddress && (
                    <small className="text-danger">
                      {formErrors.streetAddress}
                    </small>
                  )}
                </Col>
              </Row>

              <Row className="mt-1">
                <Col md={6}>
                  <label>City</label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-2"
                    required
                  />
                  {formErrors.city && (
                    <small className="text-danger">{formErrors.city}</small>
                  )}
                </Col>
                <Col md={6}>
                  <label>Country</label>
                  <div className="dropdown">
                    <Dropdown
                      value={formData.country}
                      onChange={(e) => handleCountrySelect(e.value)}
                      options={options}
                      optionLabel="name"
                      placeholder="Select a Country"
                      filter
                      valueTemplate={countryValueTemplate}
                      itemTemplate={countryOptionTemplate}
                      className="mt-2 w-full md:w-14rem w-100 text-start px-3 border rounded-5 bg-white text-secondary"
                    />
                  </div>
                  {formErrors.country && (
                    <small className="text-danger">{formErrors.country}</small>
                  )}
                </Col>
              </Row>

              <Row className="mt-1">
                <Col md={6}>
                  <label>State/Province</label>
                  <div className="dropdown">
                    <Button
                      className={`bg-white text-secondary form-control form-select text-start px-3 rounded-5 border mt-1 ${
                        formErrors.state ? "border-danger" : ""
                      }`}
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      variant=""
                    >
                      {formData.state || "Select State"}
                    </Button>
                    <ul className="dropdown-menu w-100 rounded-4 hide-scroll shadow border-0">
                      {countriesStateData
                        .find((c) => c.name === formData.country)
                        ?.states?.map((state) => (
                          <li
                            key={state.id}
                            className="dropdown-item"
                            onClick={() => handleStateSelect(state.name)}
                          >
                            {state.name}
                          </li>
                        ))}
                    </ul>
                    {formErrors.state && (
                      <small className="text-danger">{formErrors.state}</small>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <label>Postal Code</label>
                  <input
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="form-control rounded-5 mt-2"
                    required
                  />
                  {formErrors.postalCode && (
                    <small className="text-danger">
                      {formErrors.postalCode}
                    </small>
                  )}
                </Col>
              </Row>

              <div className="mt-3 d-flex justify-content-center">
                <Button
                  className="btn btn-primary mt-3 mb-3 rounded-5 px-4"
                  type="submit"
                  disabled={!isFormValid}
                >
                  Update Profile
                </Button>
              </div>
            </Form>
          </Col>
          <Col md={3}></Col>
        </Row>
      </Container>
      <HelpBtn />
      <Footer full />
    </>
  );
};

export default UserProfilePage;
